import React, { useState, useEffect } from 'react';
import { doc, getDoc, getDocs, collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { User, ShoppingBag, LogOut, Mail, Phone, MapPin, Calendar, DollarSign } from 'lucide-react';
import './CustomerProfile.css';

const CustomerProfile = () => {
  const [customerData, setCustomerData] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadCustomerProfile = async () => {
      try {
        const user = auth.currentUser;
        if (!user) {
          setError('No customer logged in');
          setLoading(false);
          return;
        }

        // Get customer data by email
        const customersRef = collection(db, 'customers');
        const q = query(customersRef, where('email', '==', user.email));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const customerDoc = querySnapshot.docs[0];
          const customer = { id: customerDoc.id, ...customerDoc.data() };
          setCustomerData(customer);

          // Load customer orders
          const ordersRef = collection(db, 'orders');
          const ordersQuery = query(ordersRef, where('customerId', '==', customer.id), orderBy('createdAt', 'desc'));
          
          const unsubscribeOrders = onSnapshot(ordersQuery, (snapshot) => {
            const ordersList = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            setOrders(ordersList);
          });

          return () => unsubscribeOrders();
        } else {
          setError('Customer profile not found');
        }
      } catch (err) {
        console.error('Error loading customer profile:', err);
        setError('Failed to load profile');
      } finally {
        setLoading(false);
      }
    };

    loadCustomerProfile();
  }, []);

  const handleSignOut = async () => {
    try {
      await auth.signOut();
      window.location.href = '/';
    } catch (err) {
      console.error('Error signing out:', err);
      setError('Failed to sign out');
    }
  };

  if (loading) {
    return (
      <div className="customer-profile-page">
        <div className="loading-profile">
          <div className="spinner"></div>
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="customer-profile-page">
        <div className="profile-header">
          <h2>Customer Profile</h2>
        </div>
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="customer-profile-page">
      <div className="profile-header">
        <h2><User size={24} /> My Profile</h2>
      </div>

      <div className="profile-content">
        {/* Customer Info */}
        <div className="customer-info">
          <div className="info-card">
            <h3><User size={20} /> Personal Information</h3>
            <div className="info-row">
              <span className="label">Name:</span>
              <span className="value">{customerData.name}</span>
            </div>
            <div className="info-row">
              <Mail size={16} />
              <span className="value">{customerData.email}</span>
            </div>
            <div className="info-row">
              <Phone size={16} />
              <span className="value">{customerData.phone}</span>
            </div>
            <div className="info-row">
              <MapPin size={16} />
              <span className="value">
                {customerData.address?.line1}, {customerData.address?.city}, {customerData.address?.state} {customerData.address?.postal_code}
              </span>
            </div>
            <div className="info-row">
              <Calendar size={16} />
              <span className="value">Customer since {new Date(customerData.createdAt?.toDate()).toLocaleDateString()}</span>
            </div>
          </div>

          {/* Customer Stats */}
          <div className="stats-card">
            <h3><ShoppingBag size={20} /> Shopping Stats</h3>
            <div className="stats-grid">
              <div className="stat-item">
                <div className="stat-number">{customerData.totalOrders || 0}</div>
                <div className="stat-label">Total Orders</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">${(customerData.totalSpent || 0).toFixed(2)}</div>
                <div className="stat-label">Total Spent</div>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Orders */}
        <div className="orders-section">
          <h3><ShoppingBag size={20} /> Recent Orders</h3>
          {orders.length === 0 ? (
            <div className="no-orders">
              <ShoppingBag size={48} />
              <p>No orders yet</p>
            </div>
          ) : (
            <div className="orders-list">
              {orders.slice(0, 5).map(order => (
                <div key={order.id} className="order-card">
                  <div className="order-header">
                    <span className="order-id">#{order.id.slice(-8)}</span>
                    <span className={`order-status ${order.status}`}>
                      {order.status}
                    </span>
                  </div>
                  <div className="order-details">
                    <div className="order-date">
                      <Calendar size={14} />
                      {new Date(order.createdAt?.toDate()).toLocaleDateString()}
                    </div>
                    <div className="order-total">
                      <DollarSign size={14} />
                      ${(order.total || 0).toFixed(2)}
                    </div>
                  </div>
                  <div className="order-items">
                    {order.items?.length} items
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Sign Out Button */}
        <div className="profile-actions">
          <button className="sign-out-btn" onClick={handleSignOut}>
            <LogOut size={20} />
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomerProfile;
